import { useMemo, useState } from "react";
import { ArrowLeft, Pencil, ShoppingBag, RotateCcw, Plus, Star, Clock, Bike, Search } from "lucide-react";
import { DEMO_CATEGORIES, DEMO_MENU, DEMO_RESTAURANT, DEMO_BANNER } from "@/data/demoMenu";
import { ProductRow } from "@/components/demo/ProductRow";
import { CartPanel } from "@/components/demo/CartPanel";
import { waLink, WHATSAPP_NUMBER } from "@/lib/whatsapp";
import { LogoMark } from "@/components/Logo";
import { ChatWidget } from "@/components/ChatWidget";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export default function DemoCardapio() {
    const [menu, setMenu] = useState(DEMO_MENU);
    const [cart, setCart] = useState({});
    const [owner, setOwner] = useState(false);
    const [cat, setCat] = useState("pizzas");
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [form, setForm] = useState({ nome: "", endereco: "", pagamento: "Pix", troco: "", obs: "" });

    const lines = useMemo(
        () => menu.filter((m) => cart[m.id] > 0).map((m) => ({ ...m, qty: cart[m.id] })),
        [menu, cart],
    );
    const subtotal = lines.reduce((s, l) => s + l.preco * l.qty, 0);
    const count = lines.reduce((s, l) => s + l.qty, 0);

    const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
    const dec = (id) =>
        setCart((c) => {
            const n = { ...c, [id]: Math.max(0, (c[id] || 0) - 1) };
            if (!n[id]) delete n[id];
            return n;
        });
    const editItem = (id, field, value) =>
        setMenu((m) => m.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
    const removeItem = (id) => {
        setMenu((m) => m.filter((it) => it.id !== id));
        setCart((c) => { const n = { ...c }; delete n[id]; return n; });
    };
    const addItem = () =>
        setMenu((m) => [
            ...m,
            { id: `novo-${Date.now()}`, cat, nome: "Novo item", desc: "Descreva o produto aqui", preco: 9.9, img: DEMO_BANNER },
        ]);
    const resetDemo = () => { setMenu(DEMO_MENU); setCart({}); setSearch(""); };

    const goToCategory = (id) => {
        setCat(id);
        setSearch("");
        document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const query = search.trim().toLowerCase();
    const match = (m) =>
        !query || m.nome.toLowerCase().includes(query) || m.desc.toLowerCase().includes(query);

    const waUrl = useMemo(() => {
        const total = subtotal + DEMO_RESTAURANT.taxaEntrega;
        const msg = [
            `*NOVO PEDIDO — ${DEMO_RESTAURANT.nome}*`,
            "",
            ...lines.map((l) => `• ${l.qty}x ${l.nome} — ${brl.format(l.preco * l.qty)}`),
            "",
            `Subtotal: ${brl.format(subtotal)}`,
            `Taxa de entrega: ${brl.format(DEMO_RESTAURANT.taxaEntrega)}`,
            `*Total: ${brl.format(total)}*`,
            "",
            `*Cliente:* ${form.nome}`,
            form.endereco && `*Endereço:* ${form.endereco}`,
            `*Pagamento:* ${form.pagamento}`,
            form.pagamento === "Dinheiro" && form.troco && `*Troco para:* ${form.troco}`,
            form.obs && `*Obs:* ${form.obs}`,
        ]
            .filter(Boolean)
            .join("\n");
        return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    }, [lines, subtotal, form]);

    return (
        <div data-testid="demo-page" className="min-h-screen bg-[#F7F7F7] font-sans text-gray-800 antialiased">
            <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-[#07090E]/90 backdrop-blur-xl">
                <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4 sm:px-5">
                    <a href="/" data-testid="demo-back-link" className="flex items-center gap-2 text-xs font-medium text-slate-400 transition-colors hover:text-cyan-300 sm:text-sm">
                        <ArrowLeft size={16} />
                        Voltar ao site
                    </a>
                    <span className="hidden text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 sm:block">
                        Demonstração Strondex Tech
                    </span>
                    <button
                        data-testid="demo-owner-toggle"
                        onClick={() => setOwner(!owner)}
                        className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-all duration-300 ${
                            owner
                                ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-300"
                                : "border-white/15 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300"
                        }`}
                    >
                        <Pencil size={12} />
                        {owner ? "Modo Dono: ATIVO" : "Simular Modo Dono"}
                    </button>
                </div>
            </header>

            <div className="mx-auto max-w-4xl pb-40">
                <div className="relative">
                    <img src={DEMO_BANNER} alt="Mesa do Sabor da Vila com pratos variados" className="h-44 w-full object-cover sm:h-56" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <span
                        data-testid="demo-cover-badge"
                        className="absolute left-4 top-4 rounded-full bg-black/70 px-3.5 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-white backdrop-blur-sm sm:left-6"
                    >
                        Cardápio de demonstração
                    </span>
                </div>

                <div className="bg-white px-4 pb-4 shadow-sm sm:px-6">
                    <div className="flex items-end gap-4">
                        <div className="-mt-9 flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-4 border-white bg-[#07090E] shadow-lg">
                            <span className="text-xl font-extrabold text-cyan-400">SV</span>
                        </div>
                        <div className="min-w-0 flex-1 pt-2.5">
                            <div className="flex items-center gap-2">
                                <h1 data-testid="demo-restaurant-name" className="truncate text-xl font-extrabold text-gray-900 sm:text-2xl">
                                    {DEMO_RESTAURANT.nome}
                                </h1>
                                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-bold text-green-600">
                                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                    Aberto
                                </span>
                            </div>
                            <p className="mt-0.5 text-xs text-gray-400">Restaurante · Pizzaria · Doceria — Exemplo de demonstração</p>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1 font-semibold text-gray-700">
                            <Star size={13} className="fill-yellow-400 text-yellow-400" />
                            4.9
                            <span className="font-normal text-gray-400">(327 avaliações)</span>
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock size={13} />
                            30–45 min
                        </span>
                        <span className="flex items-center gap-1">
                            <Bike size={13} />
                            Entrega {brl.format(DEMO_RESTAURANT.taxaEntrega)}
                        </span>
                        <span className="font-semibold text-green-600">Pedido sem comissão de apps</span>
                    </div>

                    <div className="relative mt-4">
                        <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            data-testid="demo-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar no cardápio"
                            className="w-full rounded-full border border-gray-200 bg-gray-50 py-3 pl-11 pr-4 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-[#EA1D2C]/60 focus:bg-white"
                        />
                    </div>
                </div>

                <div data-testid="demo-custom-note" className="mx-4 mt-4 rounded-2xl border border-cyan-200 bg-cyan-50 p-4 sm:mx-6 sm:p-5">
                    <p className="text-sm leading-relaxed text-cyan-950 sm:text-[15px]">
                        <b>Isto é apenas uma demonstração.</b> O cardápio do seu negócio
                        será 100% personalizado pela Strondex Tech — feito do seu jeito,
                        para combinar com a sua marca:
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {["Suas fotos", "Suas cores", "Seu logotipo", "Seu estilo", "Seus produtos e preços", "Suas regras de entrega"].map((chip) => (
                            <span key={chip} className="rounded-full border border-cyan-300 bg-white px-3.5 py-1.5 text-xs font-bold text-cyan-800">
                                {chip}
                            </span>
                        ))}
                    </div>
                    <p className="mt-3 text-xs text-cyan-800/80 sm:text-sm">
                        E muito mais: o sistema é próprio, então praticamente qualquer
                        ideia pode ser desenvolvida sob medida para o seu comércio.
                    </p>
                </div>

                {owner && (
                    <div data-testid="demo-owner-banner" className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:mx-6">
                        <p className="text-sm text-amber-800">
                            <b>Painel do dono:</b> clique nos nomes, descrições e preços para editar. No sistema real, as mudanças aparecem na hora para os clientes.
                        </p>
                        <button onClick={resetDemo} data-testid="demo-reset-button" className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3.5 py-1.5 text-xs font-bold text-amber-700 hover:bg-amber-100">
                            <RotateCcw size={12} />
                            Restaurar demo
                        </button>
                    </div>
                )}

                <nav
                    data-testid="demo-categories"
                    className="scrollbar-none sticky top-14 z-40 flex gap-2 overflow-x-auto border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur-md sm:px-6"
                    aria-label="Categorias do cardápio"
                >
                    {DEMO_CATEGORIES.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`demo-category-${c.id}`}
                            onClick={() => goToCategory(c.id)}
                            className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-all duration-300 ${
                                cat === c.id && !query
                                    ? "bg-[#EA1D2C] text-white shadow-md shadow-red-200"
                                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </nav>

                <div className="mt-2">
                    {DEMO_CATEGORIES.map((c) => {
                        const items = menu.filter((m) => m.cat === c.id && match(m));
                        if (!items.length && !owner) return null;
                        return (
                            <section key={c.id} id={`cat-${c.id}`} className="scroll-mt-32 pt-4">
                                <h2 className="px-4 pb-1 pt-2 text-base font-extrabold text-gray-900 sm:px-6">{c.label}</h2>
                                <div className="bg-white">
                                    {items.map((item) => (
                                        <ProductRow
                                            key={item.id}
                                            item={item}
                                            qty={cart[item.id] || 0}
                                            owner={owner}
                                            onAdd={add}
                                            onDec={dec}
                                            onEdit={editItem}
                                            onRemove={removeItem}
                                        />
                                    ))}
                                    {owner && (
                                        <button
                                            data-testid={`demo-add-item-${c.id}`}
                                            onClick={() => { setCat(c.id); addItem(); }}
                                            className="m-4 flex w-[calc(100%-2rem)] items-center justify-center gap-2 rounded-2xl border border-dashed border-[#EA1D2C]/40 bg-red-50/50 p-4 text-sm font-bold text-[#EA1D2C] transition-colors hover:bg-red-50"
                                        >
                                            <Plus size={16} strokeWidth={3} />
                                            Adicionar item em {c.label}
                                        </button>
                                    )}
                                </div>
                            </section>
                        );
                    })}
                    {query && !menu.some(match) && (
                        <p data-testid="demo-search-empty" className="px-6 py-14 text-center text-sm text-gray-400">
                            Nenhum item encontrado para “{search}”.
                        </p>
                    )}
                </div>

                <div className="mx-4 mt-8 rounded-2xl bg-[#07090E] p-6 text-center sm:mx-6">
                    <p className="text-sm text-slate-300">
                        Gostou? Este é um exemplo do cardápio digital que a <b className="text-white">Strondex Tech</b> cria para o seu negócio — <b className="text-[#25D366]">sem taxas de marketplace</b>.
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400">
                        Demonstração genérica: no seu projeto escolhemos juntos as fotos,
                        as cores, o logotipo, o estilo e cada detalhe do cardápio. E sem
                        taxa alguma sobre vendas — você paga apenas pelo desenvolvimento.
                    </p>
                    <a
                        href={waLink("cardapio")}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="demo-quero-um-assim"
                        className="mt-4 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-[#052e12] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#2ce06f]"
                    >
                        <LogoMark size={18} />
                        Quero um cardápio assim
                    </a>
                </div>
            </div>

            {count > 0 && (
                <button
                    data-testid="demo-cart-bar"
                    onClick={() => setOpen(true)}
                    className="fixed inset-x-4 bottom-5 z-[60] mx-auto flex max-w-4xl items-center justify-between rounded-full bg-[#EA1D2C] px-6 py-4 font-bold text-white shadow-[0_14px_44px_rgba(234,29,44,0.45)] transition-transform duration-300 hover:-translate-y-0.5"
                >
                    <span className="flex items-center gap-2.5 text-sm">
                        <ShoppingBag size={19} strokeWidth={2.5} />
                        {count} {count === 1 ? "item" : "itens"}
                    </span>
                    <span className="text-sm">Ver sacola</span>
                    <span data-testid="demo-cart-total" className="text-base font-extrabold">{brl.format(subtotal)}</span>
                </button>
            )}

            <CartPanel open={open} lines={lines} subtotal={subtotal} form={form} setForm={setForm} waUrl={waUrl} onClose={() => setOpen(false)} />
            <ChatWidget />
        </div>
    );
}
